import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Contact } from "./ContactForm.api";
import { useAltcha, Honeypot } from "components/core/Altcha/AltchaWidget";
import { toastrError, toastrSuccess } from "./../../lib/toastr";
import { FiArrowRight, FiHelpCircle, FiShield } from "react-icons/fi";
const intialState = {
  name: "",
  email: "",
  question: "",
  altcha: "",
  contactReason: "",
  errors: {
    name: "",
    email: "",
    question: "",
  },
};

type FormState = typeof intialState;
type ContactPayload = Omit<FormState, "errors">;
export function ContactForm() {
  const { t } = useTranslation();
  const { widget: altchaWidget, solve: solveAltcha } = useAltcha();
  const [formState, setFormState] = useState<FormState>(intialState);
  const postQuery = useMutation(
    (payload: ContactPayload) => {
      return Contact({
        name: payload.name,
        email: payload.email,
        question: payload.question,
        altcha: payload.altcha,
        contactReason: payload.contactReason,
      });
    },
    {
      onSuccess: (response: any) => {
        if (response.success) {
          toastrSuccess(response.message);
          setFormState(intialState);
        } else {
          toastrError(response.message);
        }
      },
      onError: (err: any) => {
        const repsonse: any = err.response;
        toastrError(repsonse?.data.message);
      },
    }
  );
  // function onChangeToken(value) {
  //   setFormState({ ...formState, ["captchaToken"]: value });
  // }
  const validation = (formValues: FormState) => {
    setFormState((old) => {
      return { ...old, errors: { ...old.errors, name: "", email: "", question: "" } };
    });

    if (formValues.name === "") {
      setFormState((old) => {
        return { ...old, errors: { ...old.errors, name: t("home.contact.Names") } };
      });
      return false;
    }

    // email validation patterns
    const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    if (!(formValues.email && regexEmail.test(formValues.email))) {
      setFormState((old) => {
        return { ...old, errors: { ...old.errors, email: t("home.contact.email") } };
      });
      return false;
    }

    // if (!formValues.question.length) {
    //   setFormState((old) => {
    //     return { ...old, errors: { ...old.errors, question: t("home.contact.Questions") } };
    //   });
    //   return false;
    // }

    if (formValues.question === "") {
      setFormState((old) => {
        return { ...old, errors: { ...old.errors, question: t("home.contact.Questions") } };
      });
      return false;
    }

    // if (!formValues.captchaToken.length) {
    //   setFormState((old) => {
    //     return { ...old, errors: { ...old.errors, captchaToken: t("home.contact.Captcha") } };
    //   });
    //   return false;
    // }

    return true;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const valid = validation(formState);
    if (!valid) return;

    // Solve a fresh challenge immediately before sending. The server enforces
    // single use, so a payload cannot be reused across submissions.
    const altcha = await solveAltcha();
    if (!altcha) {
      toastrError(t("home.contact.Captcha"));
      return;
    }

    postQuery.mutate({ ...formState, altcha });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState((prevFormValue) => ({
      ...prevFormValue,
      [name]: value,
      errors: {
        ...prevFormValue.errors,
        [name]: null,
      },
    }));
  };

  return (
    <section id="contact" className="w-full py-16 border-t scroll-mt-24 bg-surface border-line sm:py-24">
      <div className="grid gap-10 px-4 mx-auto max-w-7xl sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        {/* Info */}
        <div className="lg:col-span-5">
          <p className="zs-eyebrow">Contact</p>
          <h2 className="mt-1 text-3xl font-bold tracking-tight font-display text-ink sm:text-4xl">
            {t("home.contact.AnyQuestion")}
          </h2>
          <ul className="mt-8 space-y-3">
            <li>
              <a href="/#faq" className="flex items-center gap-4 p-4 zs-card zs-card-interactive">
                <span className="zs-stat-icon">
                  <FiHelpCircle aria-hidden="true" />
                </span>
                <span className="flex-1 text-sm font-semibold text-ink">{t("home.header.FAQ")}</span>
                <FiArrowRight aria-hidden="true" className="text-ink-4" />
              </a>
            </li>
            <li>
              <a href="/privacy-policy" className="flex items-center gap-4 p-4 zs-card zs-card-interactive">
                <span className="zs-stat-icon">
                  <FiShield aria-hidden="true" />
                </span>
                <span className="flex-1 text-sm font-semibold text-ink">{t("home.footer.PrivacyPolicy")}</span>
                <FiArrowRight aria-hidden="true" className="text-ink-4" />
              </a>
            </li>
          </ul>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <div className="p-5 shadow-md zs-card sm:p-8">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="Name" className="zs-label zs-label-required">
                  {t("home.contact.Name")}
                </label>
                <input
                  className="zs-input"
                  placeholder={t("home.contact.form.name")}
                  id="name"
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  aria-invalid={formState.errors.name ? "true" : undefined}
                />
                {formState.errors.name ? (
                  <p className="zs-error" id="email_err_msg">
                    {formState.errors.name}
                  </p>
                ) : null}
              </div>
              <div>
                <label className="zs-label zs-label-required">{t("home.contact.Email")}</label>
                <input
                  className="zs-input"
                  placeholder={t("home.contact.form.email")}
                  name="email"
                  id="email"
                  value={formState.email}
                  onChange={handleChange}
                  aria-invalid={formState.errors.email ? "true" : undefined}
                />
                {formState.errors.email ? (
                  <p className="zs-error" id="email_err_msg">
                    {formState.errors.email}
                  </p>
                ) : null}
              </div>
              <div>
                <label className="zs-label zs-label-required">{t("home.contact.Question")}</label>
                <textarea
                  className="zs-textarea"
                  placeholder={t("home.contact.form.question")}
                  name="question"
                  id="question"
                  value={formState.question}
                  onChange={handleChange}
                  aria-invalid={formState.errors.question ? "true" : undefined}
                />
                {formState.errors.question ? (
                  <p className="zs-error" id="email_err_msg">
                    {formState.errors.question}
                  </p>
                ) : null}
              </div>
              <div className="flex w-full">{altchaWidget}</div>
              <Honeypot
                value={formState.contactReason}
                onChange={(v) => setFormState((old) => ({ ...old, contactReason: v }))}
              />
              <button
                type="submit"
                disabled={postQuery.isLoading}
                className="zs-btn zs-btn-primary zs-btn-lg zs-btn-block"
              >
                {postQuery.isLoading ? t("claim.card.Loading") : t("home.contact.SUBMIT")}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
